import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import PWAInstallPrompt from '../components/PWAInstallPrompt';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { 
  Plus, Users, QrCode, Edit, Trash2, Download, 
  ArrowLeft, Eye, Copy, CheckCircle2
} from 'lucide-react';
import { getAuthHeaders } from '../lib/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactGroups = () => {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [copiedUrl, setCopiedUrl] = useState(false);

  useEffect(() => {
    loadGroups();
  }, []);

  const loadGroups = async () => {
    try {
      const response = await axios.get(`${API}/contact-groups`, {
        headers: getAuthHeaders()
      });
      setGroups(response.data.groups || []);
    } catch (error) {
      toast.error('Failed to load contact groups');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm('Are you sure you want to delete this group?')) return;

    try {
      await axios.delete(`${API}/contact-groups/${groupId}`, {
        headers: getAuthHeaders()
      });
      toast.success('Group deleted successfully');
      loadGroups();
    } catch (error) {
      toast.error('Failed to delete group');
      console.error(error);
    }
  };

  const handleShowQR = async (group) => {
    try {
      const response = await axios.get(`${API}/contact-groups/${group.id}/qr`, {
        headers: getAuthHeaders()
      });
      setQrData(response.data);
      setSelectedGroup(group);
      setShowQRModal(true);
    } catch (error) {
      toast.error('Failed to generate QR code');
      console.error(error);
    }
  };

  const handleCopyUrl = () => {
    if (qrData?.groupUrl) {
      navigator.clipboard.writeText(qrData.groupUrl);
      setCopiedUrl(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopiedUrl(false), 2000);
    }
  };

  const handleDownloadQR = () => {
    if (qrData?.dataUrl) {
      const link = document.createElement('a');
      link.href = qrData.dataUrl;
      link.download = `${selectedGroup?.name.replace(/\s+/g, '_')}_QR.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('QR code downloaded!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <p className="text-slate-300">Loading groups...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-8">
      {/* PWA Install Prompt */}
      <PWAInstallPrompt
        appName="Contact Groups"
        appIcon="👥"
        appColor="#06b6d4"
        storageKey="contactgroups-install-prompt"
      />
      
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/digi-connect')}
              className="text-slate-300 hover:text-white hover:bg-slate-800"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-white" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                Contact Groups
              </h1>
              <p className="text-slate-400 mt-1">Share multiple contacts at once with QR codes</p>
            </div>
          </div>
          <Button
            onClick={() => navigate('/contact-groups/create')}
            className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white shadow-lg shadow-cyan-500/50"
          >
            <Plus className="w-5 h-5 mr-2" />
            Create Group
          </Button>
        </div>

        {/* Groups List */}
        {groups.length === 0 ? (
          <Card className="p-12 bg-slate-800/50 backdrop-blur-sm border border-slate-700 text-center">
            <Users className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No Contact Groups Yet</h3>
            <p className="text-slate-400 mb-6">Create your first group to share multiple contacts at once</p>
            <Button
              onClick={() => navigate('/contact-groups/create')}
              className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white"
            >
              <Plus className="w-5 h-5 mr-2" />
              Create Your First Group
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {groups.map((group) => (
              <Card key={group.id} className="p-6 bg-slate-800/50 backdrop-blur-sm border border-slate-700 hover:border-cyan-500 transition-all">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center">
                      <Users className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white">{group.name}</h3>
                      <p className="text-sm text-slate-400">{group.contacts?.length || 0} contacts</p>
                    </div>
                  </div>
                </div>

                {group.description && (
                  <p className="text-sm text-slate-300 mb-4 line-clamp-2">{group.description}</p>
                )}

                <div className="flex gap-2">
                  <Button
                    onClick={() => handleShowQR(group)}
                    size="sm"
                    className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
                  >
                    <QrCode className="w-4 h-4 mr-1" />
                    QR Code
                  </Button>
                  <Button
                    onClick={() => navigate(`/contact-groups/${group.id}`)}
                    size="sm"
                    variant="outline"
                    className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white"
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    View
                  </Button>
                </div>

                <div className="flex gap-2 mt-2">
                  <Button
                    onClick={() => navigate(`/contact-groups/edit/${group.id}`)}
                    size="sm"
                    variant="outline"
                    className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white"
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                  <Button
                    onClick={() => handleDeleteGroup(group.id)}
                    size="sm"
                    variant="outline"
                    className="flex-1 border-red-500/50 text-red-400 hover:bg-red-500/20"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* QR Code Modal */}
        {showQRModal && qrData && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowQRModal(false)}>
            <Card className="bg-slate-800 border border-slate-700 p-8 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-2xl font-bold text-white mb-2" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                {selectedGroup?.name}
              </h3>
              <p className="text-slate-400 mb-6">
                Scan this QR code to view and download all {selectedGroup?.contacts?.length || 0} contacts
              </p>

              {/* QR Code */}
              <div className="bg-white p-4 rounded-xl mb-6">
                <img
                  src={qrData.dataUrl}
                  alt="Group QR Code"
                  className="w-full h-auto"
                />
              </div>

              {/* Share URL */}
              <div className="mb-6">
                <label className="text-sm font-semibold text-slate-300 mb-2 block">Share Link:</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={qrData.groupUrl}
                    readOnly
                    className="flex-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-300 text-sm"
                  />
                  <Button
                    onClick={handleCopyUrl}
                    size="sm"
                    className="bg-slate-700 hover:bg-slate-600"
                  >
                    {copiedUrl ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <Button
                  onClick={handleDownloadQR}
                  className="flex-1 bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download QR
                </Button>
                <Button
                  onClick={() => setShowQRModal(false)}
                  variant="outline"
                  className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white"
                >
                  Close
                </Button>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactGroups;
